import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../types";
import logo from "../assets/EidosGo_Logo_Transparent.png";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
    >
      <style>
        {`
          .nav-links {
            display: flex;
            gap: 0.4rem;
            align-items: center;
          }

          .nav-link {
            background: transparent;
            border: none;
            color: rgba(255, 255, 255, 0.85);
            cursor: pointer;
            font-size: 0.92rem;
            font-weight: 600;
            padding: 0.6rem 0.95rem 0.55rem;
            border-radius: 12px 12px 6px 6px;
            position: relative;
            transition: color 0.2s ease, transform 0.2s ease;
          }

          .nav-link::after {
            content: "";
            position: absolute;
            left: 16%;
            right: 16%;
            bottom: 6px;
            height: 3px;
            border-radius: 999px;
            background: linear-gradient(90deg, #60a5fa, #22c55e);
            opacity: 0;
            transform: scaleX(0.6);
            transition: all 0.2s ease;
          }

          .nav-link:hover {
            color: #ffffff;
            transform: translateY(-1px);
          }

          .nav-link:hover::after {
            opacity: 1;
            transform: scaleX(1);
          }

          .user-icon-button {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 1px solid rgba(255, 255, 255, 0.2);
            background: rgba(255, 255, 255, 0.08);
            display: grid;
            place-items: center;
            color: white;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .user-icon-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.2);
          }

          .user-menu button:hover {
            background: rgba(15, 23, 42, 0.04);
          }
        `}
      </style>
      <nav
        style={{
          padding: "0.75rem 2rem",
          background:
            "linear-gradient(120deg, #0f172a 0%, #0f1b2d 45%, #13263b 100%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1.5rem",
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
          ["--primary" as any]: "#60a5fa",
          ["--secondary" as any]: "#22c55e",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <img
            src={logo}
            alt="EidosGo"
            style={{ width: "80px", height: "auto", display: "block" }}
          />
          <span
            style={{
              fontSize: "1.5rem",
              fontWeight: 700,
              background:
                "linear-gradient(90deg, var(--primary), var(--secondary))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            EidosGo
          </span>
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          {user && (
            <>
              <div className="nav-links">
                <button
                  onClick={() => navigate("/downloads")}
                  className="nav-link"
                >
                  Home
                </button>
                {user.role === UserRole.SUPERADMIN && (
                  <>
                    <button
                      onClick={() => navigate("/users")}
                      className="nav-link"
                    >
                      Users
                    </button>
                    <button
                      onClick={() => navigate("/invoices")}
                      className="nav-link"
                    >
                      Invoices
                    </button>
                  </>
                )}
                {user.role === UserRole.ADMIN && (
                  <>
                    <button
                      onClick={() => navigate("/users")}
                      className="nav-link"
                    >
                      Users
                    </button>
                    <button
                      onClick={() => navigate("/billing")}
                      className="nav-link"
                    >
                      Billing
                    </button>
                  </>
                )}
                {user.role === UserRole.USER && (
                  <>
                  </>
                )}
              </div>
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setShowUserMenu((prev) => !prev)}
                  className="user-icon-button"
                  aria-label="User menu"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M20 21a8 8 0 0 0-16 0" />
                    <circle cx="12" cy="8" r="4" />
                  </svg>
                </button>
                {showUserMenu && (
                  <div style={userMenuStyle} className="user-menu">
                    <div style={userMenuHeaderStyle}>
                      <div style={userAvatarStyle}>
                        {user.firstName?.charAt(0)}
                        {user.lastName?.charAt(0)}
                      </div>
                      <div>
                        <div style={userNameStyle}>
                          {user.firstName} {user.lastName}
                        </div>
                        <div style={userRoleStyle}>{user.role}</div>
                      </div>
                    </div>
                    <button
                      style={userMenuItemStyle}
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate("/profile");
                      }}
                    >
                      Profile
                    </button>
                    <button
                      style={userMenuItemStyle}
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </nav>
      <main style={{ flex: 1, padding: "2rem" }}>{children}</main>
    </div>
  );
};

const userMenuStyle: React.CSSProperties = {
  position: "absolute",
  right: 0,
  top: "48px",
  minWidth: "220px",
  background: "white",
  borderRadius: "16px",
  boxShadow: "0 20px 40px rgba(15, 23, 42, 0.2)",
  border: "1px solid rgba(15, 23, 42, 0.08)",
  overflow: "hidden",
  zIndex: 10,
};

const userMenuHeaderStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  padding: "14px 16px",
  background: "linear-gradient(120deg, #eef2ff, #ecfeff)",
  borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
};

const userAvatarStyle: React.CSSProperties = {
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: "linear-gradient(120deg, #60a5fa, #22c55e)",
  color: "white",
  display: "grid",
  placeItems: "center",
  fontWeight: 700,
  fontSize: "0.9rem",
};

const userNameStyle: React.CSSProperties = {
  fontWeight: 700,
  color: "#0f172a",
};

const userRoleStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#64748b",
};

const userMenuItemStyle: React.CSSProperties = {
  width: "100%",
  textAlign: "left",
  border: "none",
  background: "white",
  padding: "0.65rem 1rem",
  fontSize: "0.92rem",
  cursor: "pointer",
  color: "#0f172a",
};


export default Layout;
